<template>
  <Modal @cancel="emit('cancel')">
    <template v-slot:title>
      <div class="media">
        <div class="media-left">
          <figure class="image is-48x48">
            <svg class="app-icon">
              <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#contenthighlights"></use>
            </svg>
          </figure>
        </div>
        <div class="media-content">
          <p class="title is-4"><strong>Content Highlights</strong></p>
          <p class="subtitle is-6">Highlight important content to customers within your Web Assistant</p>
        </div>
      </div>
    </template>
    <template v-slot:body>
      <ContentHighlightsArticleAppEdit
        v-if="isArticleApp() && articleAppConfig"
        v-model="articleAppConfig"
        @isInvalid="handleInvalid"
      />
      <ContentHighlightsListAppEdit
        v-if="!isArticleApp() && listAppConfig"
        v-model="listAppConfig"
        :value="listAppConfig"
        @isInvalid="handleInvalid"
      />
    </template>
    <template v-slot:buttons>
      <n-button-group>
        <n-button variant="primary" :loading="isLoading" :disabled="isInvalid" @click="saveApp">Update</n-button>
        <n-button variant="primary--outline" @click="emit('cancel')">Cancel</n-button>
      </n-button-group>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import { ref, PropType, onMounted } from 'vue';
import Modal from '@/shared/components/Modal.vue';
import ContentHighlightsArticleAppEdit from './ContentHighlightsArticleAppEdit.vue';
import ContentHighlightsListAppEdit from './ContentHighlightsListAppEdit.vue';
import { ArticleAppConfig } from './models/ArticleApp';
import { ListApp, ListAppItem } from './models/ListApp';
import useVuelidate from '@vuelidate/core';

const props = defineProps({
  appConfig: {
    type: Object as PropType<ConfiguredAppIntegration>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'save', app: ConfiguredAppIntegration): void;
  (e: 'cancel'): void;
}>();

const isLoading = ref(false);
const isInvalid = ref(false);
const articleAppConfig = ref<ArticleAppConfig>();
const listAppConfig = ref<ListApp>();

const $v = useVuelidate();

onMounted(() => {
  if (isArticleApp()) {
    articleAppConfig.value = {
      image_url: props.appConfig.config.image_url,
      header_text: props.appConfig.config.header_text,
      body_text: props.appConfig.config.body_text,
      article_url: props.appConfig.config.article_url,
      link_label: props.appConfig.config.link_label,
      $templateName: 'SingleHighlight',
    };
  } else {
    parseListAppConfig();
  }
});

function isArticleApp(): boolean {
  return props.appConfig.config.$templateName === 'SingleHighlight';
}

async function saveApp() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    return;
  }
  emit(
    'save',
    isArticleApp()
      ? {
          config: {
            ...articleAppConfig.value,
          },
          installedAppId: props.appConfig.installedAppId,
        }
      : getListAppFromConfig(),
  );
}

function handleInvalid(value: boolean) {
  isInvalid.value = value;
}

function parseListAppConfig() {
  const items: ListAppItem[] = [
    {
      url: '',
      description: '',
      label: '',
      image: '',
    },
  ];

  for (const [key, value] of Object.entries(props.appConfig.config).filter(([k]) => k.includes('url'))) {
    const parts = key.split('_');
    if (!items[Number(parts[parts.length - 1]) - 1]) {
      items[Number(parts[parts.length - 1]) - 1] = {
        url: '',
        description: '',
        label: '',
        image: '',
      };
    }
    items[Number(parts[parts.length - 1]) - 1][parts[parts.length - 2] as keyof ListAppItem] = value as string;
  }

  listAppConfig.value = {
    heading: props.appConfig.config.header,
    $templateName: 'ListOfHighlights',
    items,
  };
}

function getListAppFromConfig(): ConfiguredAppIntegration {
  const connectListApp: ConfiguredAppIntegration = {
    installedAppId: props.appConfig.installedAppId,
    config: {
      header: listAppConfig.value?.heading,
      $templateName: 'ListOfHighlights',
    },
  };

  if (!listAppConfig.value?.items) {
    throw new Error("App doesn't contain any items");
  }

  for (let i = 0; i < listAppConfig.value.items.length; i += 1) {
    connectListApp.config[`url_${i + 1}`] = listAppConfig.value.items[i].url;
    connectListApp.config[`url_description_${i + 1}`] = listAppConfig.value.items[i].description;
    connectListApp.config[`url_label_${i + 1}`] = listAppConfig.value.items[i].label;
    connectListApp.config[`url_image_${i + 1}`] = listAppConfig.value.items[i].image;
  }

  return connectListApp;
}
</script>

<style lang="scss" scoped>
.app-icon {
  width: 3rem;
  height: 3rem;
}

:deep(.modal-card-head) {
  .delete {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  }
}
</style>
